import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { api, itemImageUrl } from '../api/client';
import { useApi } from '../api/useApi';
import { useDiscoveryState } from '../api/useDiscoveryState';
import AsyncSection from '../components/AsyncSection';
import EmiroLogo from '../components/EmiroLogo';
import { CatalogueRowStrip } from '../components/media/CatalogueBrowser';
import MediaRow from '../components/media/MediaRow';
import Spotlight from '../components/media/Spotlight';

/// What everyone sees first: what is new, and what there is. Deliberately free of server
/// internals - version, hardware and libraries belong to whoever runs the server.
export default function DashboardPage() {
  const { t } = useTranslation();
  const recent = useApi(['items', 'recent', 12], () => api.recentItems(12));
  const continueWatching = useApi(['items', 'continue'], () => api.continueWatching(12));
  const { playsOnDemand } = useDiscoveryState();

  // Where everything plays on demand, the shelf being empty says nothing: the catalogue below is
  // what there is to watch, and "your library is empty" over it would be simply untrue.
  const isEmpty = !playsOnDemand
    && (recent.data?.movies.length ?? 0) === 0 && (recent.data?.episodes.length ?? 0) === 0;

  // The spotlight goes to what somebody was last in the middle of, and otherwise to the newest
  // film - whichever of them has a still to stand on.
  const resumable = (continueWatching.data ?? []).find((entry) => itemImageUrl(entry.item, 'Backdrop'));
  const newest = (recent.data?.movies ?? []).find((movie) => itemImageUrl(movie, 'Backdrop'));
  const spotlight = resumable
    ? { item: resumable.item, progress: resumable.progress, eyebrow: t('dashboard.continueWatching') }
    : newest && { item: newest, progress: undefined, eyebrow: t('dashboard.recentMovies') };

  // Outside the stack: a stack takes the margins off what it holds, and the spotlight reaches
  // out past the page's padding with exactly those.
  return (
    <>
      {spotlight && !isEmpty && (
        <Spotlight item={spotlight.item} progress={spotlight.progress} eyebrow={spotlight.eyebrow} />
      )}

      <Stack spacing={4}>
        {!(spotlight && !isEmpty) && <Typography variant="h2">{t('dashboard.title')}</Typography>}

        <AsyncSection loading={recent.loading} error={recent.error} onRetry={recent.reload}>
          {isEmpty ? (
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                textAlign: 'center',
                gap: 2,
                py: { xs: 6, md: 10 },
                px: 3,
                borderRadius: 3,
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <EmiroLogo layout="mark" size={72} glow />
              <Typography variant="h3">{t('dashboard.empty')}</Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 520 }}>
                {t('dashboard.emptyHint')}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={4}>
              {/* First, because somebody who left something half-watched came back for it. */}
              <MediaRow
                title={t('dashboard.continueWatching')}
                items={(continueWatching.data ?? []).map((entry) => entry.item)}
              />

              {playsOnDemand ? (
                // Everything is here, so what there is to watch is what the catalogue has - not
                // what happened to be fetched last.
                <>
                  <CatalogueRowStrip title={t('dashboard.catalogue.trending')} row="Trending" />
                  <CatalogueRowStrip title={t('dashboard.catalogue.popularMovies')} row="Popular" kind="Movie" />
                  <CatalogueRowStrip title={t('dashboard.catalogue.popularSeries')} row="Popular" kind="Series" />
                  <CatalogueRowStrip title={t('dashboard.catalogue.topRated')} row="TopRated" kind="Movie" />
                </>
              ) : (
                <>
                  <MediaRow title={t('dashboard.recentMovies')} items={recent.data?.movies ?? []} />
                  <MediaRow title={t('dashboard.recentEpisodes')} items={recent.data?.episodes ?? []} />
                </>
              )}
            </Stack>
          )}
        </AsyncSection>
      </Stack>
    </>
  );
}
